import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import Baobab from 'baobab'
import Obj from './obj'

export default class Scroller extends Obj {
    constructor (opts = { el: window }) {
        super()

        gsap.registerPlugin(ScrollToPlugin)

        this._scrollDebounce = 200
        this.el = opts.el
        this.ticking = false
        this.state = new Baobab({
            scrollX: 0,
            scrollY: 0,
            directionY: null,
            directionX: null
        })

        if (typeof this.el === 'string'
                && !document.querySelector(this.el))
            return this.observe(this.el)
        else if (typeof this.el === 'string')
            this.el = document.querySelector(this.el)

        return this.listen()
    }

    observe () {
        const observer = new window.MutationObserver(() => {
            if (typeof this.el === 'string'
                    && document.querySelector(this.el)) {
                this.el = document.querySelector(this.el)
                observer.disconnect()
                this.listen()
            }
        })

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true
        })

        return this
    }

    listen () {
        this.el
            .addEventListener('scroll', this.onScroll.bind(this))

        const isTouch = ('ontouchstart' in window
                && !window.navigator.userAgent.match(/X11/))
            || ('DocumentTouch' in window
                && document instanceof window.DocumentTouch)
            || (window.navigator.maxTouchPoints || 0) > 0

        window
            .addEventListener('resize', (e) => !isTouch
                ? this.onScroll(e)
                : null)

        this.onScroll()

        return this
    }

    onScroll (e) {
        if (this.ticking)
            return

        window
            .requestAnimationFrame(() => {
                this.ticking = false
            })

        const data = Object.assign({}, this.state._data)

        const eventType = (e || {}).type || 'scroll'

        const scrollX = eventType !== 'scroll'
                && this.el === window
            ? window.scrollX || window.pageXOffset
            : this.getScroll('scrollX')

        const scrollY = eventType !== 'scroll'
                && this.el === window
            ? window.scrollY || window.pageYOffset
            : this.getScroll('scrollY')

        const getScrolledDirection = (axis) => {
            const scrollAxis = `scroll${axis}`
            const directionAxis = `direction${axis}`

            let scrollDiff = this.state.get(scrollAxis)
                - this.getScroll(scrollAxis)

            if (scrollDiff < 0)
                scrollDiff = scrollDiff * -1

            const prevScroll = axis === 'Y'
                ? scrollY
                : scrollX

            if (scrollDiff < 0.5)
                return this.state.get(directionAxis)
                        && prevScroll !== this.state.get(scrollAxis)
                    ? this.state.get(directionAxis)
                    : null

            return prevScroll < this.state.get(scrollAxis)
                ? axis === 'Y'
                    ? 'up'
                    : 'left'
                : axis === 'Y'
                    ? 'down'
                    : 'right'
        }

        const directionX = getScrolledDirection('X')
        const directionY = getScrolledDirection('Y')

        const isChange = scrollX !== data.scrollX
            || scrollY !== data.scrollY
            || directionX !== data.directionX
            || directionY !== data.directionY

        if (isChange)
            this.state.set({
                scrollX: scrollX,
                scrollY: scrollY,
                directionX: directionX,
                directionY: directionY
            })

        if (this._scrollDebounce
                && (eventType === 'scroll'
                    || isChange)) {
            clearTimeout(this._onScrollDebounce)

            this._onScrollDebounce = setTimeout(() =>
                this.emit('scroll:debounced'), this._scrollDebounce)
        }

        this.ticking = true
    }

    getScroll (dir) {
        // Scroll{Y,X} only on window
        if (this.el === window && dir in window)
            return window[dir]

        // Scroll{Y,X} fallback, only on window
        const pageOffset = this.el === window
            ? (dir === 'scrollY'
                ? window.pageYOffset
                : window.pageXOffset)
            : null

        if (pageOffset)
            return pageOffset

        // Update dir to target overflow elements and/or legacy browsers
        dir = dir === 'scrollY'
            ? 'scrollTop'
            : 'scrollLeft'

        if (this.el[dir] >= 0)
            return this.el[dir]

        // Legacy browsers only
        return ((document.compatMode || '') === 'CSS1Compat')
            ? document.documentElement[dir]
            : document.body[dir]
    }

    scrollTo (coordinates = { x: 0, y: 0 }, time = 0.8, ease = 'power3') {
        coordinates.x = coordinates.x || 0
        coordinates.y = coordinates.y || 0

        if ((this.state.get('scrollX') === coordinates.x
                && this.state.get('scrollY') === coordinates.y)
                    || this.scrollingTo)
            return Promise.resolve()

        return new Promise((resolve) => {
            if (!this.el)
                return resolve()

            // Fallback & Accessibility Scroll To
            if (!time
                    || (('matchMedia' in window)
                        && window.matchMedia('(prefers-reduced-motion: reduce)')
                            .matches)) {
                if (this.el === window)
                    window.scrollTo(coordinates.x, coordinates.y)
                else {
                    this.el.scrollLeft = coordinates.x
                    this.el.scrollTop = coordinates.y
                }

                return resolve()
            }

            const el = this.el === window
                ? document.documentElement
                : this.el

            // Animated scroll flag
            el.setAttribute('data-scrolling-to', true)
            this.scrollingTo = true

            // Animated scroll
            gsap.to(this.el, {
                duration: time,
                scrollTo: coordinates,
                ease: ease,
                onComplete: () => {
                    el.removeAttribute('data-scrolling-to')
                    this.scrollingTo = false
                    this.onScroll()
                    resolve()
                    this.emit('scrollingTo:complete')
                }
            })
        })
    }
}
